import React, { useEffect, useState } from 'react';
import { Router, createHistory, LocationProvider } from '@reach/router';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ReactGA from 'react-ga';
import { useTranslation } from 'react-i18next';

import styles from './Page.module.scss';
import { updateData } from '../../proto/utils/updates';
import Devices from './Devices/Devices';
import History from './History/History';
import MainPage from './MainPage/MainPage';
import EmptyPage from './EmptyPage/EmptyPage';
import LoadingPage from './LoadingPage/LoadingPage';
import ResetPage from './ResetPage/ResetPage';
import Tabs from './Tabs/Tabs';
import Folder from './Folder/Folder';
import RequiresFeature, { Features } from './Utils/RequiresFeature';

const pageHistory = createHistory(window);
if (process.env.APP_ENV === 'production') {
  ReactGA.pageview(pageHistory.location.pathname);
  pageHistory.listen(({ location }) => {
    ReactGA.pageview(location.pathname);
  });
}

function Page({ isLoggedIn }) {
  const { t } = useTranslation();

  const [tabs, setTabs] = useState([]);
  const [history, setHistory] = useState([]);
  const [devices, setDevices] = useState([]);
  const [speeddials, setSpeeddials] = useState();
  const [bookmarks, setBookmarks] = useState();
  const [counters, setCounters] = useState({});

  const onDataUpdate = (data) => {
    if (data) {
      setCounters({
        tabs: data.tabs,
        history: data.history,
        devices: data.devices.count,
        preferences: data.preferences.count,
        passwords: data.passwords.count,
        speeddials: data.speeddials,
        bookmarks: data.bookmarks,
      });
      setTabs(data.tabs.data);
      setHistory(data.history.data);
      setDevices(data.devices.data);
      setSpeeddials(data.speeddials.data);
      setBookmarks(data.bookmarks.data);
    } else {
      setCounters({});
      setTabs([]);
      setHistory([]);
      setDevices([]);
      setSpeeddials();
      setBookmarks();
    }
  };

  const isPageNotEmpty = () => Object.values(counters).reduce(
    (accumulator, currentValue) => {
      if (typeof currentValue === 'object') return accumulator + currentValue.count;
      return accumulator + currentValue;
    }, 0,
  );

  const dataWereFetched = () => Object.keys(counters).length !== 0;

  useEffect(() => {
    if (isLoggedIn) {
      updateData(onDataUpdate);
    }
  }, [isLoggedIn]);

  const GridNotSupported = (
    <div className={styles.gridNotSupported}>
      <span>{t('Your browser is not supported. Please update your browser to newest version.')}</span>
    </div>
  );

  return (
    <RequiresFeature features={[Features.DISPLAY_GRID]} substitute={GridNotSupported}>
      { isLoggedIn && dataWereFetched()
        ? (
          isPageNotEmpty()
            ? (
              <LocationProvider history={pageHistory}>
                <Router basepath={process.env.REACT_APP_BASE_PATH}>
                  <MainPage default counters={counters} />
                  <ResetPage path="reset" />
                  {
                    counters.tabs && !counters.tabs.encrypted
                    && <Tabs path="tabs" tabs={tabs} />
                  }
                  {
                    counters.history && !counters.history.encrypted
                    && <History path="history" history={history} />
                  }
                  <Devices path="devices" devices={devices} />
                  {
                    counters.speeddials && !counters.speeddials.encrypted
                    && <Folder path="speeddials" folderTree={speeddials} caption="Speed Dials" />
                  }
                  {
                    counters.bookmarks && !counters.bookmarks.encrypted
                    && <Folder path="bookmarks" folderTree={bookmarks} caption="Bookmarks" />
                  }
                </Router>
              </LocationProvider>
            ) : (
              <EmptyPage />
            )
        ) : (
          <LoadingPage />
        )}
    </RequiresFeature>
  );
}

Page.propTypes = {
  isLoggedIn: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  isLoggedIn: state.isLoggedIn,
});

export default connect(mapStateToProps)(Page);
